// The governed menu panel. Placement and sizing belong to CSS anchor
// positioning (the `menu-popup` utility); this hook supplies the reviewed
// disclosure, dismissal and keyboard behaviour.
//
// It follows the W3C APG Menu Button and Menu patterns: opening moves focus
// into the panel, arrow keys rove between items, Escape closes and returns
// focus to the trigger, and Tab closes and lets focus continue past it. A
// disabled item stays reachable so its presence can be learned, but cannot be
// activated.

export const MenuPopup = {
  mounted() {
    this.bind()

    this.onTriggerClick = (event) => {
      // A menu is commonly placed inside another interactive surface (for
      // example, a Card that opens its detail modal). Disclosure belongs to
      // the trigger and must not activate that enclosing surface.
      event.stopPropagation()
      if (!this.supportsPopover) this.toggle()
    }
    this.onTriggerKeydown = (event) => this.triggerKeydown(event)
    this.onPanelKeydown = (event) => this.panelKeydown(event)
    // Capture phase: LiveView listens on `document`, so an aria-disabled item
    // has to be stopped before the event leaves the panel. A native `disabled`
    // attribute would do this for free but would also make the item
    // unfocusable, which the contract forbids.
    this.onPanelClickCapture = (event) => {
      const item = event.target.closest("[data-menu-item]")
      if (!item) return

      if (this.isDisabled(item)) {
        event.preventDefault()
        event.stopPropagation()
      } else {
        this.close({ restoreFocus: false })
      }
    }
    this.onToggle = (event) => {
      const open = event.newState === "open"
      this.setExpanded(open)
      // `toggle` is queued after showPopover(), so open() has already placed
      // focus and a keyboard user may have moved it on. Only an opening that
      // bypassed open() still needs focus moved into the panel.
      if (open && !this.panel.contains(document.activeElement)) {
        this.focusItem(0, 1)
      }
    }

    this.trigger.addEventListener("click", this.onTriggerClick)
    this.trigger.addEventListener("keydown", this.onTriggerKeydown)
    this.panel.addEventListener("keydown", this.onPanelKeydown)
    this.panel.addEventListener("click", this.onPanelClickCapture, true)
    if (this.supportsPopover) {
      this.panel.addEventListener("toggle", this.onToggle)
    } else {
      this.panel.hidden = true
    }

    this.onDocumentPointerDown = (event) => {
      if (!this.el.contains(event.target) && this.isOpen()) {
        this.close({ restoreFocus: false })
      }
    }
    document.addEventListener("pointerdown", this.onDocumentPointerDown)
  },

  updated() {
    this.bind()
  },

  destroyed() {
    document.removeEventListener("pointerdown", this.onDocumentPointerDown)
    if (this.supportsPopover) {
      this.panel.removeEventListener("toggle", this.onToggle)
    }
  },

  bind() {
    this.trigger = this.el.querySelector("[data-menu-trigger]")
    this.panel = this.el.querySelector("[data-menu-panel]")
    this.items = Array.from(this.el.querySelectorAll("[data-menu-item]"))
    this.supportsPopover =
      typeof this.panel.showPopover === "function" &&
      typeof this.panel.hidePopover === "function"
  },

  isDisabled(item) {
    return item.getAttribute("aria-disabled") === "true"
  },

  isOpen() {
    return this.supportsPopover
      ? this.panel.matches(":popover-open")
      : !this.panel.hidden
  },

  setExpanded(open) {
    this.trigger.setAttribute("aria-expanded", String(open))
  },

  // `index` is the item to focus once open. The APG makes this the author's
  // job: a menu is not visually persistent, so Tab never reaches into it.
  open(index = 0) {
    if (this.trigger.disabled) return

    if (this.supportsPopover) {
      if (!this.isOpen()) this.panel.showPopover()
    } else {
      this.panel.hidden = false
    }

    this.setExpanded(true)
    this.focusItem(index, 1)
  },

  close({ restoreFocus = true } = {}) {
    if (this.supportsPopover) {
      if (this.isOpen()) this.panel.hidePopover()
    } else {
      this.panel.hidden = true
    }

    this.setExpanded(false)
    if (restoreFocus) this.trigger.focus()
  },

  toggle() {
    this.isOpen() ? this.close() : this.open()
  },

  triggerKeydown(event) {
    if (
      event.key === "Enter" ||
      event.key === " " ||
      event.key === "ArrowDown"
    ) {
      event.preventDefault()
      this.open(0)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.open(this.items.length - 1)
    } else if (event.key === "Escape" && this.isOpen()) {
      event.preventDefault()
      this.close()
    }
  },

  panelKeydown(event) {
    const current = this.items.indexOf(document.activeElement)

    if (event.key === "Escape") {
      event.preventDefault()
      this.close()
    } else if (event.key === "ArrowDown") {
      event.preventDefault()
      this.focusItem(current + 1, 1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.focusItem(current - 1, -1)
    } else if (event.key === "Home") {
      event.preventDefault()
      this.focusItem(0, 1)
    } else if (event.key === "End") {
      event.preventDefault()
      this.focusItem(this.items.length - 1, -1)
    } else if (event.key === "Tab") {
      this.close({ restoreFocus: false })
    }
  },

  // An aria-disabled item is still focusable — the APG keeps it reachable so
  // its presence can be learned — so this wraps rather than skipping.
  // `direction` is retained for callers that seek from an edge.
  focusItem(index, _direction) {
    const length = this.items.length
    if (length === 0) return

    const target = this.items[((index % length) + length) % length]
    if (target) target.focus()
  },
}
